import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { FC, useMemo } from 'react';

import { GeneratedTaxType } from '../../../types/generated';
import { ProductPrice } from '../../ProductPrice/ProductPrice';

interface BasketReviewSummaryProps {
  shipping: number;
  subtotal: number;
  subTotalAfterTaxAndShipping: number;
  taxRate?: number;
  taxAmount: number;
  taxType: GeneratedTaxType;
}

export const BasketReviewSummary: FC<BasketReviewSummaryProps> = ({
  shipping,
  subTotalAfterTaxAndShipping,
  subtotal,
  taxAmount,
  taxRate,
  taxType,
}) => {
  const { t } = useTranslation('product');
  const rate = taxRate ? `(${taxRate}%)` : '';

  const lines = useMemo(() => {
    const breakdownLines: {
      key: string;
      value: string | React.ReactElement;
      className?: string;
    }[] = [
      {
        key: t('drawers.review.breakdown.subTotal'),
        value: (
          <ProductPrice inMinorUnit price={subtotal} skipConvertCurrency />
        ),
      },
      {
        key: t('drawers.review.shipping'),
        value: shipping ? (
          <ProductPrice inMinorUnit price={shipping} skipConvertCurrency />
        ) : (
          t('drawers.review.free')
        ),
      },
    ];

    if (taxType === GeneratedTaxType.Ddp && taxAmount > 0) {
      breakdownLines.push({
        key: t('drawers.review.breakdown.duty'),
        value: (
          <ProductPrice
            inMinorUnit
            price={taxAmount}
            skipConvertCurrency
            skipTaxCalculation
          />
        ),
      });
    }

    breakdownLines.push({
      key: t('drawers.review.breakdown.total'),
      value: (
        <ProductPrice
          inMinorUnit
          price={subTotalAfterTaxAndShipping}
          skipConvertCurrency
          skipTaxCalculation
        />
      ),
      className: 'font-bold',
    });

    if (taxType === GeneratedTaxType.Vat && taxAmount > 0) {
      breakdownLines.push({
        key: `${t('drawers.review.breakdown.vat')} ${rate}`,
        value: (
          <ProductPrice
            inMinorUnit
            price={taxAmount}
            skipConvertCurrency
            skipTaxCalculation
          />
        ),
        className: 'text-neutral-4',
      });
    }

    return breakdownLines;
  }, [
    shipping,
    subTotalAfterTaxAndShipping,
    subtotal,
    taxAmount,
    rate,
    taxType,
  ]);

  return (
    <ol className="flex flex-col gap-2">
      {lines.map(({ className, key, value }) => (
        <li key={key} className={classNames('flex justify-between', className)}>
          <span>{key}</span>
          <span>{value}</span>
        </li>
      ))}
    </ol>
  );
};
